/**
 * Color palette module for consistent series color assignment.
 * Provides static colors and color utilities for charts.
 */

import { getChartLogger } from "./debug";

// ==================== Static Color Palette ====================

/**
 * Default color used when no color is specified (blue).
 */
export const DEFAULT_CHART_COLOR = "#2563eb";

/**
 * Static palette of visually distinct colors for chart series.
 * Colors are chosen for visual distinction, accessibility, and professional appearance.
 */
export const CHART_COLORS: readonly string[] = [
  // Blue (primary)
  DEFAULT_CHART_COLOR,
  // Blue
  "#0032F1",
  // Mustard
  "#AE8E00",
  // Brown
  "#97542B",
  // Lime
  "#65a30d",
  // Purple
  "#8317B2",
  // Orange
  "#FF6200",
  // Pink
  "#FF008C",
  // Sky
  "#0094D9",
] as const;

/**
 * Default colors for candlestick charts.
 */
export const CANDLESTICK_COLORS = {
  // Green - price increased
  up: "#338736",
  // Red - price decreased
  down: "#E51503",
  // Gray - no change
  unchanged: "#767676",
  // Dark gray for wicks
  wick: "#999999",
} as const;

// ==================== Color Selection ====================

/**
 * Get color for a series at a given index.
 * If the series has a provided color, use it; otherwise, assign from palette.
 */
export function getSeriesColor(index: number, providedColor?: string): string {
  if (providedColor && isValidHexColor(providedColor)) {
    return normalizeColor(providedColor);
  }

  return CHART_COLORS[index % CHART_COLORS.length];
}

// ==================== Color Validation ====================

/**
 * Validate a hex color string.
 * Accepts: #RGB, #RRGGBB, #RRGGBBAA
 */
export function isValidHexColor(color: string): boolean {
  return /^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(color);
}

/**
 * Normalize color to 6-digit hex format.
 * #RGB → #RRGGBB
 */
export function normalizeColor(color: string): string {
  // Already 6 or 8 digit hex
  if (/^#[0-9A-F]{6}([0-9A-F]{2})?$/i.test(color)) {
    return color.toUpperCase();
  }

  // 3-digit hex: #RGB → #RRGGBB
  if (/^#[0-9A-F]{3}$/i.test(color)) {
    const r = color[1];
    const g = color[2];
    const b = color[3];

    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  // Handle rgb/rgba formats (basic support)
  const rgbMatch = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/i.exec(color);

  if (rgbMatch?.[1] && rgbMatch[2] && rgbMatch[3]) {
    const r = Number.parseInt(rgbMatch[1], 10).toString(16).padStart(2, "0");
    const g = Number.parseInt(rgbMatch[2], 10).toString(16).padStart(2, "0");
    const b = Number.parseInt(rgbMatch[3], 10).toString(16).padStart(2, "0");

    return `#${r}${g}${b}`.toUpperCase();
  }

  // Fallback: return as-is
  getChartLogger().warn(`Unrecognized color format passed through: ${color}`);

  return color;
}

// ==================== Candlestick Colors ====================

/**
 * Get candlestick color based on open/close comparison.
 */
export function getCandleColor(open: number, close: number): string {
  if (close > open) return CANDLESTICK_COLORS.up;
  if (close < open) return CANDLESTICK_COLORS.down;

  return CANDLESTICK_COLORS.unchanged;
}

/**
 * Get volume bar color based on price movement.
 * Matches candlestick colors for visual consistency.
 */
export function getVolumeBarColor(
  currentClose: number,
  previousClose?: number
): string {
  if (previousClose === undefined) {
    return CANDLESTICK_COLORS.unchanged;
  }

  if (currentClose > previousClose) return CANDLESTICK_COLORS.up;
  if (currentClose < previousClose) return CANDLESTICK_COLORS.down;

  return CANDLESTICK_COLORS.unchanged;
}

// ==================== Color Manipulation ====================

/**
 * Add opacity to a hex color.
 * @param color - Hex color (#RRGGBB)
 * @param opacity - 0 to 1
 * @returns Hex color with alpha (#RRGGBBAA)
 */
export function colorWithOpacity(color: string, opacity: number): string {
  const normalized = normalizeColor(color);
  const hex = normalized.replace("#", "");

  // Ensure opacity is in range [0, 1]
  const clampedOpacity = Math.max(0, Math.min(1, opacity));
  const alpha = Math.round(clampedOpacity * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

  return `#${hex.substring(0, 6)}${alpha}`;
}

/**
 * Convert hex to rgba string for CSS.
 */
export function hexToRgba(color: string, opacity: number): string {
  const normalized = normalizeColor(color);
  const hex = normalized.replace("#", "");

  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);

  const clampedOpacity = Math.max(0, Math.min(1, opacity));

  return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`;
}

// ==================== Direction-Based Colors ====================

/**
 * Direction color type for price movement.
 */
export type PriceDirection = "positive" | "negative" | "neutral";

/**
 * Get price direction based on first and last values.
 */
export function getPriceDirection(
  firstValue: number,
  lastValue: number
): PriceDirection {
  const change = lastValue - firstValue;

  if (change > 0) return "positive";
  if (change < 0) return "negative";

  return "neutral";
}

/**
 * Get line color based on price direction.
 * Used for single mode series lines.
 */
export function getDirectionLineColor(direction: PriceDirection): string {
  switch (direction) {
    case "positive":
      return CANDLESTICK_COLORS.up;
    case "negative":
      return CANDLESTICK_COLORS.down;
    case "neutral":
    default:
      return CANDLESTICK_COLORS.unchanged;
  }
}

// ==================== Contrast Calculation ====================

/**
 * Calculate relative luminance of a color.
 * Uses WCAG formula for contrast calculation.
 */
function getLuminance(hexColor: string): number {
  const hex = normalizeColor(hexColor).replace("#", "");
  const r = Number.parseInt(hex.substring(0, 2), 16) / 255;
  const g = Number.parseInt(hex.substring(2, 4), 16) / 255;
  const b = Number.parseInt(hex.substring(4, 6), 16) / 255;

  const toLinear = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Get a contrasting text color (black or white) for a given background.
 * Uses relative luminance calculation.
 */
export function getContrastColor(
  backgroundColor: string
): "#000000" | "#ffffff" {
  const luminance = getLuminance(backgroundColor);

  return luminance > 0.179 ? "#000000" : "#ffffff";
}
